import { ExpandMoreRounded } from "@mui/icons-material";
import { Box, Collapse, Paper, Typography } from "@mui/material";
import { useState } from "react";
import {
    KboAgentInvocation,
    KboFlowRunOutput,
} from "../KboFlowDebug/KboFlowRunDebug";
import { kboFlowRunDetailColors as pageColors } from "./KboFlowRunDetailStyles";

/**
 * The model calls behind one step. Pass {@link questionId} to narrow to the calls
 * that named that question; omit it for steps whose calls cover every question at
 * once (question generation, for one), where per-question lists would repeat the
 * same call verbatim.
 */
export function QuestionStepCalls({
    output,
    questionId,
    phase,
}: {
    output: KboFlowRunOutput;
    questionId?: string;
    phase: string | string[];
}) {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const normalizedQuestionId = questionId?.toLowerCase();
    const phases = (Array.isArray(phase) ? phase : [phase]).map((value) =>
        value.toLowerCase(),
    );
    const calls = (output.phaseRecords ?? []).flatMap(
        (phaseRecord, phaseIndex) =>
            (phaseRecord.invocations ?? [])
                .filter(
                    (call) =>
                        !normalizedQuestionId ||
                        call.questionIds?.some(
                            (id) => id.toLowerCase() === normalizedQuestionId,
                        ),
                )
                .filter(() =>
                    phases.includes((phaseRecord.phase ?? "").toLowerCase()),
                )
                .map((call, index) => ({
                    call,
                    key: `${phaseIndex}-${call.label ?? "call"}-${
                        call.startedAtUtc ?? index
                    }`,
                })),
    );

    if (calls.length === 0) return null;

    return (
        <Box
            sx={{
                borderTop: "1px dashed",
                borderColor: "divider",
                mt: 2,
                pt: 1.5,
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    color: pageColors.muted,
                    fontWeight: 800,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                }}
            >
                Model calls · prompts and context
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    mt: 0.75,
                }}
            >
                {calls.map(({ call, key }) => (
                    <InvocationCall
                        key={key}
                        call={call}
                        expanded={!!expanded[key]}
                        onToggle={() =>
                            setExpanded((current) => ({
                                ...current,
                                [key]: !current[key],
                            }))
                        }
                    />
                ))}
            </Box>
        </Box>
    );
}

export function InvocationCall({
    call,
    expanded,
    onToggle,
}: {
    call: KboAgentInvocation;
    expanded: boolean;
    onToggle: () => void;
}) {
    const failed = !!call.error || call.status?.toLowerCase() === "failed";
    const response =
        typeof call.response === "string"
            ? call.response
            : call.response
            ? JSON.stringify(call.response, null, 2)
            : undefined;

    return (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            <Box
                component="button"
                type="button"
                onClick={onToggle}
                sx={{
                    alignItems: "center",
                    bgcolor: "transparent",
                    border: 0,
                    color: pageColors.ink,
                    cursor: "pointer",
                    display: "flex",
                    font: "inherit",
                    gap: 0.75,
                    p: 1,
                    textAlign: "left",
                    width: "100%",
                }}
            >
                <ExpandMoreRounded
                    sx={{
                        fontSize: 18,
                        transform: expanded ? "rotate(180deg)" : "none",
                        transition: "transform 120ms",
                    }}
                />
                <Typography
                    variant="body2"
                    sx={{
                        // A failed call keeps its signal through the label colour
                        // rather than a status badge on every row.
                        color: failed ? "error.main" : undefined,
                        flex: 1,
                        fontWeight: 750,
                    }}
                >
                    {call.label ?? "Model call"}
                    {failed ? " · failed" : ""}
                </Typography>
            </Box>
            <Collapse in={expanded} timeout={160}>
                <Box
                    sx={{
                        borderTop: "1px solid",
                        borderColor: "divider",
                        p: 1.25,
                    }}
                >
                    <Typography variant="caption" color="text.secondary">
                        {call.model ?? "Model not recorded"}
                        {call.deployment && call.deployment !== call.model
                            ? ` · ${call.deployment}`
                            : ""}
                    </Typography>
                    <PromptBlock
                        title="System prompt"
                        value={call.systemPrompt}
                    />
                    <PromptBlock
                        title="Prompt and context sent"
                        value={call.userPrompt}
                    />
                    {response && (
                        <PromptBlock title="Model response" value={response} />
                    )}
                    {call.error && (
                        <PromptBlock title="Error" value={call.error} error />
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
}

function PromptBlock({
    title,
    value,
    error = false,
}: {
    title: string;
    value?: string;
    error?: boolean;
}) {
    if (!value) return null;
    return (
        <Box sx={{ mt: 1.25 }}>
            <Typography
                variant="caption"
                sx={{
                    color: error ? "error.main" : pageColors.muted,
                    fontWeight: 800,
                    textTransform: "uppercase",
                }}
            >
                {title}
            </Typography>
            <Box
                component="pre"
                sx={{
                    bgcolor: "#F7F8FA",
                    borderRadius: 1,
                    color: error ? "error.main" : pageColors.ink,
                    fontFamily: "monospace",
                    fontSize: 12,
                    lineHeight: 1.5,
                    m: 0,
                    mt: 0.5,
                    overflowX: "auto",
                    p: 1,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                }}
            >
                {value}
            </Box>
        </Box>
    );
}
